import React, { useState, useEffect, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Lock } from 'lucide-react';
import { toast } from 'sonner';

const LAYOUT_STORAGE_KEY = 'cartie_widget_layout';

// Sortable Widget Wrapper
const SortableWidget = ({ id, children, isDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSorting,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSorting ? 0.5 : 1,
    zIndex: isSorting ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group ${isSorting ? 'ring-2 ring-emerald-500 ring-offset-2 rounded-xl' : ''}`}
      data-testid={`draggable-widget-${id}`}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-2 right-2 z-10 p-1.5 rounded-lg bg-white/80 dark:bg-slate-800 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing border border-slate-200 dark:border-slate-700 shadow-sm"
        title="Drag to reorder"
      >
        <GripVertical className="w-4 h-4 text-slate-400" />
      </div>
      {children}
    </div>
  );
};

// Static Widget (not draggable)
const StaticWidget = ({ children }) => (
  <div className="relative">
    <div className="absolute top-2 right-2 z-10 p-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 opacity-50">
      <Lock className="w-3 h-3 text-slate-400" />
    </div>
    {children}
  </div>
);

// Main Draggable Grid Component
const DraggableWidgetGrid = ({ 
  widgets, 
  onLayoutChange, 
  columns = 2,
  gap = 4 
}) => {
  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState(null);

  // Initialize items from widgets prop
  useEffect(() => {
    // Load saved layout
    const savedLayout = localStorage.getItem(LAYOUT_STORAGE_KEY);
    let orderedIds = [];
    
    if (savedLayout) {
      try {
        orderedIds = JSON.parse(savedLayout);
      } catch (e) {
        console.error('Failed to parse saved layout');
      }
    }

    // Get widget IDs from props
    const widgetIds = widgets.map(w => w.id);
    
    // Merge saved order with current widgets
    // Keep saved order for existing widgets, append new ones at end
    const orderedItems = [];
    orderedIds.forEach(id => {
      if (widgetIds.includes(id)) {
        orderedItems.push(id);
      }
    });
    
    // Add any new widgets not in saved layout
    widgetIds.forEach(id => {
      if (!orderedItems.includes(id)) {
        orderedItems.push(id);
      }
    });

    setItems(orderedItems);
  }, [widgets]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (active.id !== over?.id) {
      setItems((prevItems) => {
        const oldIndex = prevItems.indexOf(active.id);
        const newIndex = prevItems.indexOf(over.id);
        const newItems = arrayMove(prevItems, oldIndex, newIndex);
        
        // Save to localStorage
        localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(newItems));
        
        // Notify parent
        if (onLayoutChange) {
          onLayoutChange(newItems);
        }
        
        toast.success('Widget layout saved');
        return newItems;
      });
    }
  };

  // Create widget map for quick lookup
  const widgetMap = useMemo(() => {
    const map = {};
    widgets.forEach(w => {
      map[w.id] = w;
    });
    return map;
  }, [widgets]);

  // Get active widget for drag overlay
  const activeWidget = activeId ? widgetMap[activeId] : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={rectSortingStrategy}>
        <div 
          className={`grid grid-cols-1 lg:grid-cols-${columns} gap-${gap}`}
          style={{ 
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fit, minmax(300px, 1fr))`,
            gap: `${gap * 4}px`
          }}
          data-testid="draggable-widget-grid"
        >
          {items.map((id) => {
            const widget = widgetMap[id];
            if (!widget) return null;
            
            if (widget.static) {
              return (
                <StaticWidget key={id}>
                  {widget.component}
                </StaticWidget>
              );
            }

            return (
              <SortableWidget key={id} id={id} isDragging={activeId === id}>
                {widget.component}
              </SortableWidget>
            );
          })}
        </div>
      </SortableContext>

      {/* Drag Overlay - shows preview while dragging */}
      <DragOverlay>
        {activeWidget ? (
          <div className="opacity-80 transform scale-105 shadow-2xl rounded-xl">
            {activeWidget.component}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default DraggableWidgetGrid;

// Helper to reset layout
export const resetWidgetLayout = () => {
  localStorage.removeItem(LAYOUT_STORAGE_KEY);
  toast.success('Widget layout reset to default');
  window.location.reload();
};

// Helper to get saved layout
export const getSavedLayout = () => {
  const saved = localStorage.getItem(LAYOUT_STORAGE_KEY);
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (e) {
      return null;
    }
  }
  return null;
};
